import { ChangeDetectorRef, Component, DestroyRef, HostListener, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { auditTime, filter, map } from 'rxjs/operators';

import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationStart, RouteConfigLoadEnd, RouteConfigLoadStart, Router } from '@angular/router';
import { customIcons } from '@shared/const/custom-icons.const';
import { Personas } from '@shared/models/personas.model';
import { InstanceIdService } from '@shared/services/instance-id.service';
import { SubscriptionManager } from '@shared/services/subscription-manager.service';
import { TaskService } from '@shared/services/task.service';
import { ChatService } from './core/services/chat.service';
import { NavigationService } from './core/services/navigation.service';
import { NewVersionCheckerService } from './core/services/new-version-checker.service';
import { RoutingStateService } from './core/services/routing-state.service';
import { SocketService } from './core/services/socket.service';
import { StorageService } from './core/services/storage.service';
import { HrsuiteComponent } from './hrsuite/hrsuite.component';
import { HrsuiteService } from './hrsuite/hrsuite.service';
import { deactivateComponentService } from './shared/services/desactivate.service';
import { ErrorService } from './shared/services/error.service';
import { ShareCalendarRequestService } from './shared/services/share-calendar-request.service';
import { UserService } from './shared/services/user.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  private instanceId: string;

  public isLoading: boolean = false;

  public isDarkMode: boolean = false;

  public isNewDarkMode: boolean = false;

  public isMenuEnabled: Observable<boolean> | undefined;

  public isMenuEnabledStatus: boolean = true;

  public sideNavReduced: boolean = false;

  public crToDisplayInfos: any;

  public popupXLocation: string = '';

  public popupYLocation: string = '';

  public currentChildComp: HrsuiteComponent | undefined;

  public hasUnsavedChanges: boolean = false;

  public get isThereNewModifs(): boolean {
    if (this.currentChildComp) {
      return this.currentChildComp.isThereNewModifs;
    }
    return false;
  }

  public get isConnected(): boolean {
    if (this.userService.getCurrentUser()) {
      return true;
    }

    return false;
  }

  constructor(
    private readonly iconRegistry: MatIconRegistry,
    private readonly domSanitizer: DomSanitizer,
    private changeDetectorRef: ChangeDetectorRef,
    private shareCalendarRequestService: ShareCalendarRequestService,
    private hrsuiteService: HrsuiteService,
    private navigationService: NavigationService,
    private errorService: ErrorService,
    private socketService: SocketService,
    private chatService: ChatService,
    private taskService: TaskService,
    private storageService: StorageService,
    private deactivateService: deactivateComponentService,
    private userService: UserService,
    private routingState: RoutingStateService,
    private router: Router,
    public newVersionCheckerService: NewVersionCheckerService,
    private subscriptionManager: SubscriptionManager,
    private destroyRef: DestroyRef,
    private instanceIdService: InstanceIdService
  ) {
    this.addCustomIcons();

    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
    this.routingState.loadRouting();
    this.subscribeSetSidenav();
    this.subscribeRouteChange();

    this.subscriptionManager
      .createRequest(this.shareCalendarRequestService.sharedMessage, 'sharedMessage', this.instanceId)
      .subscribe({
        next: (cr: any) => {
          if (cr && cr.hospital && cr.hospital._id) {
            cr.hospital.displayName = this.userService.resolveHospitalName(cr.hospital._id);
          }
          this.crToDisplayInfos = cr;
        },

        error: (error: any) => {
          this.errorService.handleError(error);
        },
      });

    this.subscriptionManager.createRequest(this.router.events, 'events', this.instanceId).subscribe({
      next: (event: any): void => {
        if (event instanceof RouteConfigLoadStart) {
          this.isLoading = true;
        } else if (event instanceof RouteConfigLoadEnd) {
          this.isLoading = false;
        }
      },
      error: (error: any) => {
        this.errorService.handleError(error);
      },
    });

    this.subscriptionManager
      .createRequest(this.router.events, 'events2', this.instanceId)
      .pipe(
        filter((event: any): event is NavigationStart => event instanceof NavigationStart),
        auditTime(0)
      )
      .subscribe({
        next: (event: any) => {
          if (this.isConnected && event.url !== '/authentication/sign-in') {
            this.checkAccessOptionsHash();
          }
        },
        error: (error: any) => this.errorService.handleError(error),
      });

    this.subscriptionManager
      .createRequest(this.hrsuiteService.toggleNightSubject, 'toggleNightSubject', this.instanceId)
      .subscribe({
        next: (val: boolean) => {
          this.isDarkMode = val;
          if (this.isDarkMode) {
            this.isNewDarkMode = false;
          }
        },

        error: (error: any) => {
          this.errorService.handleError(error);
        },
      });
    // Transitory new dark mode application
    this.subscriptionManager
      .createRequest(this.hrsuiteService.darkModeSubject, 'darkModeSubject', this.instanceId)
      .subscribe({
        next: (val: boolean) => {
          this.isNewDarkMode = val;
          if (this.isNewDarkMode) {
            this.isDarkMode = false;
          }
          this.changeDetectorRef.detectChanges();
        },

        error: (error: any) => {
          this.errorService.handleError(error);
        },
      });
  }

  ngOnInit(): void {
    this.subscriptionManager
      .createRequest(this.navigationService.routeChanges(), 'routeChanges2', this.instanceId)
      .pipe(
        map((event: any) => {
          this.navigationService.initNavigation(event).catch((error: any) => {
            this.errorService.handleError(error);
          });
        })
      );

    this.subscriptionManager
      .createRequest(this.deactivateService.hasUnsavedChanges, 'hasUnsavedChanges', this.instanceId)
      .subscribe({
        next: (hasChanges: boolean) => {
          this.hasUnsavedChanges = hasChanges;
        },
        error: (error: any) => this.errorService.handleError(error),
      });

    this.initSocket();
  }

  private initSocket(): void {
    const socket = this.socketService.connect();

    if (socket) {
      socket.off('connect');
      socket.on('connect', () => {
        this.chatService.connect();
        this.taskService.connect();
      });
    }
  }

  checkAccessOptionsHash() {
    this.subscriptionManager
      .createRequest(this.userService.checkAccessOptions(), 'checkAccessOptions', this.instanceId, false, 1000)
      .subscribe({
        next: async (res: Personas) => {
          if (res) {
            const currentUser = JSON.parse(JSON.stringify(this.storageService.getUser()));
            currentUser.persona = res;
            this.storageService.clearUser();
            this.storageService.storeUser(currentUser);
            this.userService.rebuildMenuSubject.next();
            await this.navigationService.reloadCurrentRoute();
          }
        },
        error: (error: any) => {
          this.errorService.simpleErrorMsg(error);
        },
      });
  }

  subscribeSetSidenav() {
    this.subscriptionManager.createRequest(this.navigationService.setSidenav, 'setSidenav', this.instanceId).subscribe({
      next: () => {
        this.isMenuEnabledStatus = !this.isMenuEnabledStatus;
      },

      error: (error: any) => {
        this.errorService.handleError(error);
      },
    });
  }

  subscribeRouteChange() {
    this.subscriptionManager
      .createRequest(this.navigationService.routeChanges(), 'routeChanges', this.instanceId)
      .subscribe({
        next: (e: any) => {
          this.isMenuEnabledStatus = !this.navigationService.hideSideNavInCurrentPage(e);

          if (this.isConnected && !this.socketService.getSocket()) {
            this.initSocket();
          }
          if (this.isNewDarkMode) {
            this.isNewDarkMode = false;
          }
        },

        error: (error: any) => {
          this.errorService.handleError(error);
        },
      });
  }

  toggleSideNav() {
    this.sideNavReduced = !this.sideNavReduced;
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.socketService.disconnect();
  }

  onActivate(componentRef: any) {
    if (componentRef instanceof HrsuiteComponent) {
      this.currentChildComp = componentRef;
    }
  }

  save() {
    this.deactivateService.switchDeactivate.next(true);
    if (this.isThereNewModifs) {
      this.currentChildComp?.save();
    }
  }

  addCustomIcons(): void {
    customIcons.forEach((icon) => {
      this.iconRegistry.addSvgIcon(icon.label, this.domSanitizer.bypassSecurityTrustResourceUrl(icon.path));
    });
  }
}
